import React, { useEffect, useState } from 'react';
import './ClaimDetailModal.scss';

const ClaimDetailModal = ({ claim, onClose, onUpdate, isUpdating }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [updatedFile, setUpdatedFile] = useState(null);
  const [formData, setFormData] = useState({
    invoiceNo: '',
    title: '',
    expenseDate: '',
    expensePurpose: '',
    currency: '€',
    amount: '',
    remarks: '',
  });

  const canEdit = claim?.status === 'Pending';

  useEffect(() => {
    if (!claim) {
      setFormData({
        invoiceNo: '',
        title: '',
        expenseDate: '',
        expensePurpose: '',
        currency: '€',
        amount: '',
        remarks: '',
      });
      setUpdatedFile(null);
      setIsEditing(false);
      return;
    }

    setFormData({
      invoiceNo: claim.invoiceNo || '',
      title: claim.title || '',
      expenseDate: claim.expenseDate ? new Date(claim.expenseDate).toISOString().slice(0, 16) : '',
      expensePurpose: claim.expensePurpose || '',
      currency: claim.currency || '€',
      amount: claim.amount || '',
      remarks: claim.remarks || '',
    });
    setUpdatedFile(null);
    setIsEditing(false);
  }, [claim]);

  if (!claim) return null;

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  const getStatusBadge = (status) => {
    const statusConfig = {
      Pending: { color: 'warning', text: 'Pending' },
      Approved: { color: 'success', text: 'Approved' },
      Rejected: { color: 'danger', text: 'Rejected' }
    };
    
    const config = statusConfig[status] || { color: 'secondary', text: status };
    return <span className={`status-badge ${config.color}`}>{config.text}</span>;
  };

  const onFieldChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const onFileChange = (e) => {
    if (!e.target.files?.[0]) return;
    setUpdatedFile(e.target.files[0]);
  };

  const handleSave = async () => {
    if (!onUpdate) return;
    await onUpdate({
      claimId: claim.id,
      claimData: {
        ...claim,
        ...formData,
        amount: parseFloat(formData.amount),
        expenseDate: formData.expenseDate,
      },
      file: updatedFile,
    });
    setIsEditing(false);
    setUpdatedFile(null);
  };
  
  return (
    <div className="modal-overlay">
      <div className="claim-detail-modal">
        <div className="modal-header">
          <h3>Claim Details</h3>
          <button className="close-button" onClick={onClose}>&times;</button>
        </div>
        
        <div className="modal-body">
          <div className="claim-header">
            <div className="claim-title">
              <h4>{claim.title}</h4>
              <div className="claim-meta">
                <div className="meta-item">
                  <span className="meta-label">Status:</span>
                  <span className="meta-value">{getStatusBadge(claim.status)}</span>
                </div>
                <div className="meta-item">
                  <span className="meta-label">Invoice No:</span>
                  <span className="meta-value">{claim.invoiceNo}</span>
                </div>
              </div>
            </div>
            
            <div className="claim-amount">
              <span className="amount-value">{claim.currency} {claim.amount.toFixed(2)}</span>
              <span className="amount-label">Total Amount</span>
            </div>
          </div>
          
          <div className="claim-details">
            <div className="details-section">
              <div className="details-row">
                <div className="details-item">
                  <span className="details-label">Expense Date:</span>
                  {isEditing ? (
                    <input
                      name="expenseDate"
                      type="datetime-local"
                      value={formData.expenseDate}
                      onChange={onFieldChange}
                    />
                  ) : (
                    <span className="details-value">{formatDate(claim.expenseDate)}</span>
                  )}
                </div>
                <div className="details-item">
                  <span className="details-label">Claim Date:</span>
                  <span className="details-value">{formatDate(claim.claimDate)}</span>
                </div>
              </div>
              
              <div className="details-row">
                <div className="details-item">
                  <span className="details-label">Expense Purpose:</span>
                  {isEditing ? (
                    <select
                      name="expensePurpose"
                      value={formData.expensePurpose}
                      onChange={onFieldChange}
                    >
                      <option value="Office Expense">Office Expense</option>
                      <option value="Business Trip">Business Trip</option>
                      <option value="Personal">Personal Expense</option>
                      <option value="Other">Other</option>
                    </select>
                  ) : (
                    <span className="details-value">{claim.expensePurpose}</span>
                  )}
                </div>
                {claim.lastUpdated && (
                  <div className="details-item">
                    <span className="details-label">Last Updated:</span>
                    <span className="details-value">{formatDate(claim.lastUpdated)}</span>
                  </div>
                )}
              </div>
              
              <div className="details-row">
                <div className="details-item">
                  <span className="details-label">Invoice No:</span>
                  {isEditing ? (
                    <input
                      name="invoiceNo"
                      type="text"
                      value={formData.invoiceNo}
                      onChange={onFieldChange}
                    />
                  ) : (
                    <span className="details-value">{claim.invoiceNo}</span>
                  )}
                </div>
                <div className="details-item">
                  <span className="details-label">Title:</span>
                  {isEditing ? (
                    <input
                      name="title"
                      type="text"
                      value={formData.title}
                      onChange={onFieldChange}
                    />
                  ) : (
                    <span className="details-value">{claim.title}</span>
                  )}
                </div>
              </div>

              <div className="details-row">
                <div className="details-item">
                  <span className="details-label">Currency:</span>
                  {isEditing ? (
                    <select name="currency" value={formData.currency} onChange={onFieldChange}>
                      <option value="€">€ (Euro)</option>
                      <option value="$">$ (USD)</option>
                      <option value="£">£ (GBP)</option>
                    </select>
                  ) : (
                    <span className="details-value">{claim.currency}</span>
                  )}
                </div>
                <div className="details-item">
                  <span className="details-label">Amount:</span>
                  {isEditing ? (
                    <input
                      name="amount"
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={formData.amount}
                      onChange={onFieldChange}
                    />
                  ) : (
                    <span className="details-value">{claim.amount.toFixed(2)}</span>
                  )}
                </div>
              </div>

              <div className="details-row">
                <div className="details-item full-width">
                  <span className="details-label">Remarks:</span>
                  {isEditing ? (
                    <textarea
                      name="remarks"
                      rows="3"
                      value={formData.remarks}
                      onChange={onFieldChange}
                    />
                  ) : (
                    <p className="details-value remarks">{claim.remarks}</p>
                  )}
                </div>
              </div>

              {isEditing && (
                <div className="details-row">
                  <div className="details-item full-width">
                    <span className="details-label">Replace Supporting Document (Optional):</span>
                    <input type="file" onChange={onFileChange} accept=".jpg,.jpeg,.png,.pdf,.doc,.docx" />
                    {updatedFile && <small className="details-value">{updatedFile.name}</small>}
                  </div>
                </div>
              )}
            </div>
            
            {claim.updatedBy && (
              <div className="details-section">
                <h5>Update Information</h5>
                <div className="details-row">
                  <div className="details-item">
                    <span className="details-label">Updated By:</span>
                    <span className="details-value">{claim.updatedBy.name}</span>
                  </div>
                  <div className="details-item">
                    <span className="details-label">Email:</span>
                    <span className="details-value">{claim.updatedBy.email}</span>
                  </div>
                </div>
              </div>
            )}
            
            {claim.supportingDoc && (
              <div className="details-section">
                <h5>Supporting Document</h5>
                <div className="document-preview">
                  <a
                    href={claim.supportingDoc}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="document-link"
                  >
                    <div className="document-icon">
                      <i className="document-icon-inner"></i>
                    </div>
                    <span>View Document</span>
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
        
        <div className="modal-footer">
          <button className="close-btn" onClick={onClose}>Close</button>
          {canEdit && !isEditing && (
            <button className="edit-btn" onClick={() => setIsEditing(true)}>Edit Claim</button>
          )}
          {isEditing && (
            <>
              <button className="close-btn" onClick={() => setIsEditing(false)} disabled={isUpdating}>
                Cancel
              </button>
              <button className="save-btn" onClick={handleSave} disabled={isUpdating}>
                {isUpdating ? 'Saving...' : 'Save Changes'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClaimDetailModal; 